import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { PossibleSettings } from '@/components/types';
import {
  settingsInit,
  settingsInitTruckOverride,
  QUICK_SETTING_PARAMS,
} from '@/components/settings-panel/settings-options';
import { z } from 'zod';

export const profileEnum = z.enum([
  'auto',
  'auto_shorter',
  'auto_pedestrian',
  'bicycle',
  'bikeshare',
  'pedestrian',
  'car',
  'truck',
  'bus',
  'hov',
  'multimodal',
  'motor_scooter',
  'motorcycle',
  'taxi',
  'transit',
]);

export type Profile = z.infer<typeof profileEnum>;

interface CommonState {
  settingsPanelOpen: boolean;
  directionsPanelOpen: boolean;
  coordinates: number[][];
  loading: boolean;
  settings: PossibleSettings;
  dateTime: { type: number; value: string };
  mapReady: boolean;
}

interface CommonActions {
  showLoading: (loading: boolean) => void;
  zoomTo: (coordinates: number[][]) => void;
  toggleSettings: () => void;
  toggleDirections: () => void;
  updateSettings: (
    name: keyof PossibleSettings,
    value: PossibleSettings[keyof PossibleSettings]
  ) => void;
  resetSettings: (profile: Profile) => void;
  updateDateTime: (key: 'type' | 'value', value: string | number) => void;
  setMapReady: (ready: boolean) => void;
}

type CommonStore = CommonState & CommonActions;

// Open the left panel by default on non-mobile viewports (Tailwind md breakpoint).
const DEFAULT_PANEL_OPEN =
  typeof window !== 'undefined' && window.innerWidth >= 768;

export const useCommonStore = create<CommonStore>()(
  devtools(
    immer((set) => ({
      settingsPanelOpen: false,
      directionsPanelOpen: DEFAULT_PANEL_OPEN,
      coordinates: [],
      loading: false,
      settings: { ...settingsInit },
      dateTime: {
        type: -1,
        value: new Date().toISOString().slice(0, 16),
      },
      mapReady: false,

      showLoading: (loading) => set({ loading }),
      zoomTo: (coordinates) => set({ coordinates }),
      setMapReady: (ready) => set({ mapReady: ready }),
      toggleSettings: () =>
        set(
          (state) => {
            state.settingsPanelOpen = !state.settingsPanelOpen;
          },
          undefined,
          'toggleSettings'
        ),
      toggleDirections: () =>
        set(
          (state) => {
            state.directionsPanelOpen = !state.directionsPanelOpen;
          },
          undefined,
          'toggleDirections'
        ),
      updateSettings: (name, value) =>
        set(
          (state) => {
            state.settings[name] = value;
          },
          undefined,
          'updateSettings'
        ),
      resetSettings: (profile) =>
        set(
          (state) => {
            const base =
              profile === 'truck' ? settingsInitTruckOverride : settingsInit;
            // Preserve quick-panel params — they're treated as cross-profile
            // user preferences (URL-permalinked) and shouldn't be wiped on a
            // profile change or an explicit Reset.
            const preserved: Partial<PossibleSettings> = {};
            for (const param of QUICK_SETTING_PARAMS) {
              preserved[param] = state.settings[param];
            }
            state.settings = { ...base, ...preserved };
          },
          undefined,
          'resetSettings'
        ),
      updateDateTime: (key, value) =>
        set(
          (state) => {
            // The Select control fires onValueChange with strings; coerce here
            // so type stays numeric (formatTriggerLabel uses === comparisons).
            if (key === 'type') state.dateTime.type = Number(value);
            else state.dateTime.value = value as string;
          },
          undefined,
          'updateDateTime'
        ),
    })),
    { name: 'common' }
  )
);
